import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import api from "../services/api";

const PAGE_SIZE = 15;

const formatMetadata = (metadata) => {
  if (!metadata || Object.keys(metadata).length === 0) {
    return "-";
  }
  return JSON.stringify(metadata, null, 2);
};

const getUserLabel = (log) => {
  if (!log) return "Sistema";
  if (log.usuario_nome) return log.usuario_nome;

  const metadata = log.metadata || {};
  const email = metadata.email;
  const cpf = metadata.cpf;

  if (email || cpf) {
    const base = email || "Usuário excluído";
    return cpf ? `${base} (${cpf})` : base;
  }

  return "Sistema";
};

function VerLogs() {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: PAGE_SIZE, total: 0, pageCount: 1 });
  const [filters, setFilters] = useState({ termo: "", data: "", acao: "" });
  const [loading, setLoading] = useState(false);
  const initialFetch = useRef(false);

  const fetchLogs = useCallback(
    async (page = meta.page, currentFilters = filters) => {
      setLoading(true);
      try {
        const { data } = await api.get("/logs", {
          params: {
            ...currentFilters,
            page,
            pageSize: PAGE_SIZE,
          },
        });
        setLogs(data.data || []);
        setMeta(data.meta || { page: 1, pageSize: PAGE_SIZE, total: 0, pageCount: 1 });
      } catch (error) {
        console.error("Erro ao buscar logs", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, meta.page]
  );

  useEffect(() => {
    if (initialFetch.current) {
      return;
    }
    initialFetch.current = true;
    fetchLogs(1, filters);
  }, [fetchLogs, filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchLogs(1, filters);
  };

  const clearFilters = () => {
    const cleared = { termo: "", data: "", acao: "" };
    setFilters(cleared);
    fetchLogs(1, cleared);
  };

  const goToPage = (direction) => {
    const nextPage = meta.page + direction;
    if (nextPage < 1 || nextPage > meta.pageCount) return;
    fetchLogs(nextPage, filters);
  };

  const hasResults = logs.length > 0;
  const paginationLabel = useMemo(() => {
    if (!meta.total) {
      return "Nenhum registro disponível";
    }
    const start = (meta.page - 1) * meta.pageSize + 1;
    const end = Math.min(meta.page * meta.pageSize, meta.total);
    return `${start} – ${end} de ${meta.total}`;
  }, [meta.page, meta.pageSize, meta.total]);

  return (
    <div className="container">
      <h1>Logs de Auditoria</h1>
      <p>
        Acompanhe todas as ações administrativas relevantes. Os registros são
        atualizados em tempo real.
      </p>

      <div className="filters-bar">
        <input
          type="search"
          value={filters.termo}
          onChange={(e) => handleFilterChange("termo", e.target.value)}
          placeholder="Buscar por ação, descrição, IP ou usuário"
        />
        <input
          type="date"
          value={filters.data}
          onChange={(e) => handleFilterChange("data", e.target.value)}
        />
        <select
          value={filters.acao}
          onChange={(e) => handleFilterChange("acao", e.target.value)}
        >
          <option value="">Todas as ações</option>
          {[...new Set(logs.map((log) => log.acao))]
            .filter(Boolean)
            .sort()
            .map((acao) => (
              <option key={acao} value={acao}>
                {acao}
              </option>
            ))}
        </select>
        <div className="spacer" />
        <button onClick={applyFilters}>Aplicar</button>
        <button className="button-secondary" onClick={clearFilters}>
          Limpar filtros
        </button>
      </div>

      <div className="surface-section log-summary">
        <strong>Resumo</strong>
        <p>{paginationLabel}</p>
      </div>

      {loading ? (
        <p>Carregando registros...</p>
      ) : hasResults ? (
        <>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Usuário</th>
                <th>Ação</th>
                <th>Descrição</th>
                <th>Detalhes</th>
                <th>Quando</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td data-label="ID">{log.id}</td>
                  <td data-label="Usuário">{getUserLabel(log)}</td>
                  <td data-label="Ação">{log.acao}</td>
                  <td data-label="Descrição">{log.descricao}</td>
                  <td data-label="Detalhes">
                    <details className="log-metadata">
                      <summary>Ver metadata</summary>
                      <pre>{formatMetadata(log.metadata)}</pre>
                    </details>
                    <span className="log-meta-info">
                      IP: {log.ip || "-"}
                    </span>
                  </td>
                  <td data-label="Quando">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="filters-bar log-pagination">
            <button
              className="button-secondary"
              onClick={() => goToPage(-1)}
              disabled={meta.page === 1}
            >
              Página anterior
            </button>
            <span>
              Página {meta.page} de {meta.pageCount}
            </span>
            <button
              onClick={() => goToPage(1)}
              disabled={meta.page === meta.pageCount}
            >
              Próxima página
            </button>
          </div>
        </>
      ) : (
        <p>Nenhum registro de auditoria encontrado.</p>
      )}
    </div>
  );
}

export default VerLogs;
